<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: HaplogroupProofBoard                                  |
| Section: components                                              |
| Purpose: Family-line proof cards for haplogroup evidence.         |
+------------------------------------------------------------------+
-->
<template>
    <section class="haplogroup-proof-board">
        <div class="haplogroup-proof-stack">
            <article v-for="line in proofLines" :key="line.id" class="haplogroup-proof-card" :class="[
                `haplogroup-proof-card--${line.lineType}`,
                `haplogroup-proof-card--${line.confidence || 'unknown'}`,
                {
                    'haplogroup-proof-card--active': selectedHaplogroupIds.includes(line.id),
                    'haplogroup-proof-card--clickable': line.routeAvailable,
                    'haplogroup-proof-card--expanded': isExpanded(line)
                }
            ]" @click="toggleLine(line)">
                <div class="haplogroup-proof-card__topline">
                    <span class="haplogroup-proof-card__type-dot" aria-hidden="true"></span>
                    <strong class="haplogroup-proof-card__compact-haplogroup">
                        {{ line.haplogroup || $t('haplogroupProof.pendingHaplogroup') }}
                    </strong>
                </div>

                <template v-if="isExpanded(line)">
                    <div class="haplogroup-proof-card__heading">
                        <h3>{{ getLineName(line) }}</h3>
                        <small v-if="line.haplogroupAncestor">
                            {{ getHaplogroupAncestorMeta(line) }}
                        </small>
                    </div>

                    <div class="haplogroup-proof-card__badges" aria-label="Haplogroup evidence">
                        <span class="haplogroup-proof-badge">{{ getSourceLabel(line.source) }}</span>
                        <span class="haplogroup-proof-badge">{{ line.testType || $t('haplogroupProof.unknownTest')
                        }}</span>
                        <span class="haplogroup-proof-badge"
                            :class="`haplogroup-proof-badge--${line.confidence || 'unknown'}`">
                            {{ getConfidenceLabel(line.confidence) }}
                        </span>
                    </div>

                    <div class="haplogroup-proof-path" :aria-label="$t('haplogroupProof.lineagePath')">
                        <div v-for="(person, personIndex) in getDisplayPath(line)" :key="person.personId || person.name"
                            class="haplogroup-proof-path__person"
                            :class="{ 'haplogroup-proof-path__person--tester': isTestedPerson(line, person) }"
                            :style="{ '--path-indent': `${personIndex * 12}px` }">
                            <a v-if="canLinkPerson(person)" :href="getPersonHref(person.personId)" @click.stop>
                                {{ getPersonDisplayName(person) }}
                            </a>
                            <strong v-else>{{ getPersonDisplayName(person) }}</strong>
                        </div>
                    </div>
                </template>
            </article>
        </div>
    </section>
</template>

<script>
import haplogroupData from '@/data/haplogroups.json';
import { useAuth } from '@/composables/useAuth';
import { hasLivingPersonAccess } from '@/utils/personPrivacy';

const LOGIN_REQUIRED_PERSON_IDS = new Set(['I753']);
const DEFAULT_EXPANDED_HAPLOGROUP = 'M7b1a1f1n1';

export default {
    name: 'HaplogroupProofBoard',
    props: {
        selectedHaplogroupIds: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['focus-route'],
    setup() {
        const { authToken } = useAuth();
        return { authToken };
    },
    data() {
        return {
            expandedLineId: this.getDefaultExpandedLineId(),
        };
    },
    computed: {
        proofLines() {
            return (haplogroupData.haplogroups || []).filter(line => !line.hidden);
        },
    },
    methods: {
        getLineTypeLabel(line) {
            return line.lineType === 'paternal'
                ? this.$t('haplogroupProof.lineTypes.paternal')
                : this.$t('haplogroupProof.lineTypes.maternal');
        },
        getLineName(line) {
            const key = `haplogroupProof.lineNames.${line.id}`;
            const translated = this.$t(key);
            return translated === key ? line.lineName || line.label : translated;
        },
        getSourceLabel(source) {
            const sourceKeyMap = {
                familytreedna: 'familytreedna',
                '23andme': 'twentyThreeAndMe',
            };
            const key = sourceKeyMap[source] || 'unknown';
            return this.$t(`haplogroupProof.sources.${key}`);
        },
        getConfidenceLabel(confidence) {
            if (!confidence) return this.$t('haplogroupProof.confidence.unknown');
            return this.$t(`haplogroupProof.confidence.${confidence}`);
        },
        getDefaultExpandedLineId() {
            return (haplogroupData.haplogroups || [])
                .find(line => !line.hidden && line.haplogroup === DEFAULT_EXPANDED_HAPLOGROUP)?.id || '';
        },
        getDisplayPath(line) {
            const path = Array.isArray(line.lineagePath) && line.lineagePath.length
                ? line.lineagePath
                : [line.testedPerson, line.linkedAncestor].filter(Boolean);
            return [...path].reverse();
        },
        isTestedPerson(line, person) {
            return Boolean(line.testedPerson?.personId && person.personId === line.testedPerson.personId);
        },
        isLoginRequiredPerson(person) {
            return Boolean(person?.personId && LOGIN_REQUIRED_PERSON_IDS.has(person.personId));
        },
        canShowPersonName(person) {
            return !this.isLoginRequiredPerson(person) || hasLivingPersonAccess(this.authToken);
        },
        canLinkPerson(person) {
            return Boolean(person?.personId && this.canShowPersonName(person));
        },
        getPersonDisplayName(person) {
            if (!this.canShowPersonName(person)) {
                return this.$t('home.nameHiddenPrivacy');
            }

            return person?.name || this.$t('placeMap.unknown');
        },
        getHaplogroupAncestorMeta(line) {
            const birth = line.haplogroupAncestor?.estimatedBirth;
            const generations = line.haplogroupAncestor?.estimatedGenerations;
            return [birth, generations ? this.$t('haplogroupProof.estimatedGenerations', { generations }) : '']
                .filter(Boolean)
                .join(' · ');
        },
        getPersonHref(personId) {
            return `/person/${encodeURIComponent(personId)}`;
        },
        isExpanded(line) {
            return this.expandedLineId === line.id;
        },
        toggleLine(line) {
            const nextExpandedId = this.isExpanded(line) ? '' : line.id;
            this.expandedLineId = nextExpandedId;

            if (nextExpandedId && line.routeAvailable) {
                this.focusRoute(line);
            }
        },
        focusRoute(line) {
            if (line.routeAvailable) {
                this.$emit('focus-route', line.id);
            }
        },
    },
};
</script>

<style scoped>
.haplogroup-proof-board {
    display: flex;
    flex-direction: column;
    width: min(360px, calc(100vw - 36px));
    max-height: calc(100% - 36px);
    pointer-events: none;
}

.haplogroup-proof-stack {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
    max-height: 100%;
    overflow-y: auto;
    pointer-events: auto;
}

.haplogroup-proof-card {
    --line-color: #777;
    display: flex;
    flex-direction: column;
    gap: 0;
    width: min(236px, 100%);
    min-width: 0;
    border: 1.5px solid color-mix(in srgb, var(--line-color) 35%, #dedede);
    border-top: 4px solid var(--line-color);
    border-radius: var(--border-radius-md, 8px);
    background: rgba(255, 255, 255, 0.94);
    box-shadow: var(--shadow-card-sm);
    padding: 10px 12px;
    max-height: 100%;
    overflow-y: auto;
    pointer-events: auto;
    backdrop-filter: blur(8px);
    transition: width 0.18s ease, padding 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.haplogroup-proof-card--clickable {
    cursor: pointer;
}

.haplogroup-proof-card--clickable:hover {
    border-color: var(--line-color);
}

.haplogroup-proof-card--paternal {
    --line-color: #0085ca;
}

.haplogroup-proof-card--maternal {
    --line-color: #e0004d;
}

.haplogroup-proof-card--speculative {
    border-style: dashed;
}

.haplogroup-proof-card--active {
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--line-color) 28%, transparent), var(--shadow-card-sm);
}

.haplogroup-proof-card--expanded {
    gap: 14px;
    width: 100%;
    padding: 16px;
}

.haplogroup-proof-card__topline {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #666;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
}

.haplogroup-proof-card__type-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--line-color);
}

.haplogroup-proof-card__compact-haplogroup {
    color: var(--line-color);
    font-size: 13px;
    line-height: 1.2;
    text-transform: none;
}

.haplogroup-proof-card__heading {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.haplogroup-proof-card h3 {
    margin: 0;
    color: var(--brand-text-dark);
    font-size: 17px;
    line-height: 1.25;
}

.haplogroup-proof-card__heading small {
    color: #666;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.35;
}

.haplogroup-proof-card__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.haplogroup-proof-badge {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    border-radius: var(--border-radius-pill);
    background: var(--brand-bg-2);
    color: #555;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 700;
}

.haplogroup-proof-badge--confirmed {
    background: color-mix(in srgb, #12805c 14%, #fff);
    color: #126047;
}

.haplogroup-proof-badge--speculative {
    background: color-mix(in srgb, #a46300 14%, #fff);
    color: #7a4a00;
}

.haplogroup-proof-path {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0;
    margin-top: auto;
}

.haplogroup-proof-path__person {
    display: flex;
    flex-direction: column;
    gap: 2px;
    position: relative;
    border-radius: var(--border-radius-md, 8px);
    background: var(--brand-bg-2);
    margin-left: var(--path-indent, 0);
    padding: 8px 10px 8px 24px;
}

.haplogroup-proof-path__person+.haplogroup-proof-path__person {
    margin-top: 8px;
}

.haplogroup-proof-path__person+.haplogroup-proof-path__person::before {
    content: '⌞';
    position: absolute;
    left: 9px;
    top: 8px;
    color: color-mix(in srgb, var(--line-color) 65%, #777);
    font-size: 14px;
    font-weight: 700;
    line-height: 1.2;
}

.haplogroup-proof-path__person--tester {
    background: color-mix(in srgb, var(--line-color) 9%, #fff);
}

.haplogroup-proof-path__person a,
.haplogroup-proof-path__person strong {
    color: var(--brand-text-dark);
    font-size: 14px;
    font-weight: 700;
    line-height: 1.3;
    text-decoration: none;
}

.haplogroup-proof-path__person a:hover {
    color: var(--brand-color-1);
}

@media (max-width: 720px) {
    .haplogroup-proof-board {
        width: min(320px, calc(100vw - 24px));
    }
}
</style>
